import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { X } from 'lucide-react';
import countryCodesData from '@/data/countryCodes.json';
import CountryFlag from 'react-country-flag';
import PhoneNumber from 'google-libphonenumber';

const countryCodes = countryCodesData.data;

const referenceSchema = z.object({
  customerName: z.string().min(1, 'Customer name is required'),
  customerEmail: z
    .string()
    .min(1, 'Customer email is required')
    .email('Please enter a valid email'),

  website: z
    .string()
    .regex(
      /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/,
      'Please enter a valid website URL',
    )
    .or(z.literal(''))
    .optional(),

  linkedin: z
    .string()
    .refine(
      (url) => {
        if (url === '') return true; // Allow empty string
        try {
          const parsedUrl = new URL(url);
          return (
            parsedUrl.hostname === 'www.linkedin.com' ||
            parsedUrl.hostname === 'linkedin.com'
          );
        } catch {
          return false;
        }
      },
      { message: 'Please enter a valid LinkedIn URL or leave it empty' },
    )
    .optional(),

  countryCode: z
    .string()
    .regex(/^\+?\d+$/, 'Country code must be a valid number')
    .optional(),

  phoneNumber: z
    .string()
    .regex(/^\d+$/, 'Phone number must contain only numbers')
    .or(z.literal(''))
    .optional(),
});

const CustomerReferenceModal = ({
  isOpen,
  onClose,
  onSubmit,
  editingReference = null,
}) => {
  const referenceForm = useForm({
    resolver: zodResolver(referenceSchema),
    defaultValues: {
      customerName: editingReference?.customerName || '',
      customerEmail: editingReference?.customerEmail || '',
      website: editingReference?.website || '',
      linkedin: editingReference?.linkedin || '',
      countryCode: editingReference?.phoneNumber?.split('-')[0] || '+91',
      phoneNumber:
        editingReference?.phoneNumber &&
        editingReference?.phoneNumber[0] === '+'
          ? editingReference?.phoneNumber?.split('-')[1]
          : '',
    },
  });

  // Utility functions
  // 1.
  const getCountryFlag = (countryCode) => {
    const matchedCountry = countryCodes.find(
      (country) => country.code === countryCode,
    );
    return matchedCountry ? matchedCountry.country : '';
  };
  // 2.
  const detectCountryCode = (country) => {
    const matchedCountry = countryCodes.find(
      (c) => c.name.toLowerCase() === country.toLowerCase(),
    );
    return matchedCountry ? matchedCountry.code : '';
  };
  // 3.
  useEffect(() => {
    const subscription = referenceForm.watch((values, { name }) => {
      if (name === 'country') {
        const country = values.country;
        if (country) {
          const matchedCountry = countryCodes.find(
            (c) => c.name.toLowerCase() === country.toLowerCase(),
          );

          if (matchedCountry) {
            referenceForm.setValue('countryCode', matchedCountry.code, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [referenceForm]);
  // 4.
  const detectCountryCodeFromNumber = (phoneNumber) => {
    const phoneUtil = PhoneNumber.PhoneNumberUtil.getInstance();

    try {
      // Attempt to parse the number
      // Use 'ZZ' as default region to allow international number parsing
      const parsedNumber = phoneUtil.parseAndKeepRawInput(phoneNumber, 'ZZ');

      if (phoneUtil.isValidNumber(parsedNumber)) {
        const countryCode = `+${parsedNumber.getCountryCode()}`;

        const regionCode = phoneUtil.getRegionCodeForNumber(parsedNumber);

        const matchedCountry = countryCodes.find(
          (country) => country.country === regionCode,
        );

        // Immediate updates
        referenceForm.setValue('countryCode', countryCode, {
          shouldValidate: true,
          shouldDirty: true,
        });

        // Update country if a match is found
        if (matchedCountry) {
          referenceForm.setValue('country', matchedCountry.name, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }

        return true;
      }
    } catch (error) {
      console.log('Phone number parsing error', error);
    }
    return false;
  };

  useEffect(() => {
    if (editingReference) {
      referenceForm.reset({
        customerName: editingReference?.name || '',
        customerEmail: editingReference?.email || '',
        website: editingReference?.website || '',
        linkedin: editingReference?.linkedin || '',
        countryCode: editingReference?.phone?.split('-')[0] || '+91',
        phoneNumber: editingReference?.phone?.split('-')[1] || '',
      });
    } else {
      referenceForm.reset({
        customerName: '',
        customerEmail: '',
        website: '',
        linkedin: '',
        countryCode: '+91',
        phoneNumber: '',
      });
    }
  }, [editingReference, referenceForm]);

  const handleSubmit = (data, event) => {
    event.preventDefault();
    event.stopPropagation();

    const formattedDate = {
      name: data.customerName,
      email: data.customerEmail,
      website: data.website,
      linkedin: data.linkedin,
      phone: data.phoneNumber ? `${data.countryCode}-${data.phoneNumber}` : '',
    };
    onSubmit(formattedDate);
    referenceForm.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-[50%] max-h-[95%] overflow-y-auto p-4"
        style={{ position: 'fixed', zIndex: 50 }}
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle className="text-darkPurple mb-6">
            {editingReference ? 'Edit Reference' : 'Add Reference 1'}
          </DialogTitle>
        </DialogHeader>

        <Form {...referenceForm}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              referenceForm.handleSubmit((data) => handleSubmit(data, e))(e);
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={referenceForm.control}
                name="customerName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Customer Contact Person</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Customer name"
                        className="focus:ring-0 focus:ring-offset-0 text-black focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={referenceForm.control}
                name="customerEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Customer email"
                        className="focus:ring-0 focus:ring-offset-0 text-black focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={referenceForm.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Website</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="customerwebsite.com"
                        className="focus:ring-0 focus:ring-offset-0 text-black focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={referenceForm.control}
                name="linkedin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>LinkedIn</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="url"
                        onChange={(e) => field.onChange(e.target.value)}
                        placeholder="Paste your LinkedIn URL"
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0  text-black focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col items-start space-y-2">
              <FormLabel>Contact Number</FormLabel>
              <div className="flex items-center border border-gray-300/70 text-gray-700 rounded-md px-3 w-full">
                <CountryFlag
                  countryCode={getCountryFlag(
                    referenceForm.watch('countryCode'),
                  )}
                  svg
                  style={{
                    width: '2em',
                    height: '2em',
                    marginRight: '0.5em',
                  }}
                />
                <FormField
                  control={referenceForm.control}
                  name="countryCode"
                  render={({ field }) => (
                    <FormItem className="flex-shrink-0">
                      <FormControl>
                        <Input
                          {...field}
                          autoComplete="new-password"
                          placeholder=""
                          onChange={(e) => {
                            // Remove non-digit characters except the leading '+'
                            const sanitizedValue = e.target.value.replace(
                              /[^\d+]/g,
                              '',
                            );
                            field.onChange({
                              target: { value: sanitizedValue },
                            });
                          }}
                          className="focus:ring-0 outline-none max-w-[60px] text-sm border-none text-black focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="h-6 border-l text-gray-700 border-[#637381] mx-3"></div>
                <FormField
                  control={referenceForm.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem className="flex-grow">
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Phone number"
                          onChange={(e) => {
                            // Remove non-digit characters
                            const sanitizedValue = e.target.value.replace(
                              /\D/g,
                              '',
                            );
                            field.onChange({
                              target: { value: sanitizedValue },
                            });

                            // Attempt to detect as soon as a reasonable number of digits are entered
                            // Adjust the length based on your requirements
                            if (
                              sanitizedValue.length >= 6 &&
                              sanitizedValue.length <= 15
                            ) {
                              detectCountryCodeFromNumber(sanitizedValue);
                            }
                          }}
                          className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple  w-full text-sm border-none"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                className="bg-purple-600 hover:bg-purple-700"
              >
                {editingReference ? 'Update' : 'Add'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerReferenceModal;
